/**
 * [NWF] Free gift coupling (NWF-119) — cart sync.
 *
 * Keeps the cart's gift lines in sync with the merchant-owned couplings. Those are maintained
 * ON the gift article ("free with these products / these collections", optional window) and
 * resolved in Liquid into hidden per-line source markers by nwf-free-gift-line.liquid:
 *
 *   - a main line in the cart → its gift products are added automatically, one gift line per
 *     main line (`_free_gift` = main product handle, `_free_gift_for` = main line, NWF-214;
 *     the app's Cart Transform prices them at zero, NWF-206)
 *   - main line removed → only its own gift lines are removed
 *   - gift line removed or requantified through the cart API while the main product is
 *     still in the cart → restored/normalised (parity with the old shop: gifts cannot be
 *     removed on their own)
 *   - gift line NOT priced at zero after sync (Cart Transform missing/inactive) → the line
 *     is removed instead of charging the customer, and the variant is flagged for the
 *     session so add/remove cannot loop
 *
 * The sync is a target/actual comparison over the whole cart, run once on page load and
 * then on every `cartUpdate` pubsub event, so it converges instead of reacting to
 * individual events. Deliberately NOT MutationObserver-driven: other components (e.g.
 * the delivery countdown) mutate the DOM every second, which would turn an observer
 * into a permanent /cart.js polling loop. The observer below is only used for the
 * cheap, network-free line styling fallback.
 */
(function () {
    'use strict';

    /* Variants that must not be retried this session: the discount did not price them at
       zero (price fail) or the cart API refused the add (add fail, e.g. product draft or
       not published to the Online Store channel). Without these flags a failing gift
       would loop forever: add → fail → resync → add … */
    var BLOCKED_KEY = 'nwfFreeGiftBlocked';

    function blockedFlags() {
        try {
            return JSON.parse(window.sessionStorage.getItem(BLOCKED_KEY) || '{}');
        } catch (error) {
            return {};
        }
    }

    function blockVariant(variantId, reason) {
        console.warn('[nwf-free-gift] blocking gift variant for this session', variantId, reason);
        try {
            var flags = blockedFlags();
            flags[variantId] = reason;
            window.sessionStorage.setItem(BLOCKED_KEY, JSON.stringify(flags));
        } catch (error) {
            /* Session storage unavailable — worst case the sync retries the add. */
        }
    }

    /* Coupling map built from the per-line source markers (nwf-free-gift-line.liquid):
       { mainHandle: [variantId, ...] }. The ids are VARIANTS — only the colours/sizes the
       merchant listed are free. The markers live at the cart line items, so they are
       re-rendered with every cart update — unlike anything at the section top, which
       cart.js does not swap. An empty map is valid: it still drives orphan removal.

       Whether a coupling is permanent or a time limited action is decided in Liquid before
       the markers are rendered, so an expired action simply is not in the map any more and
       its line is removed as an orphan on the next sync. */
    function readCouplingMap() {
        var map = {};
        document.querySelectorAll('[data-nwf-free-gift-source]').forEach(function (marker) {
            var handle = marker.dataset.nwfHandle;
            if (!handle || map[handle]) {
                return;
            }
            /* Gift products that stay at one unit regardless of the main quantity: video
               products. A video is bought once, and the withdrawal validation blocks a video
               line above 1 while asking the customer to fix a quantity that is not editable
               on a gift line — the checkout would deadlock. The discount Function caps the
               entitlement the same way, so both halves agree. */
            var singles = (marker.dataset.nwfGiftSingleProducts || '').split(',');
            /* "productId:variantId|variantId,productId:variantId" → one group per coupled
               product, because only ONE variant per product may end up in the cart. */
            map[handle] = (marker.dataset.nwfGiftGroups || '')
                .split(',')
                .map(function (group) {
                    var parts = group.split(':');
                    return {
                        productId: parseInt(parts[0], 10),
                        singleUnit: singles.indexOf(parts[0]) !== -1,
                        variantIds: (parts[1] || '')
                            .split('|')
                            .map(function (id) {
                                return parseInt(id, 10);
                            })
                            .filter(function (id) {
                                return !isNaN(id);
                            }),
                    };
                })
                .filter(function (group) {
                    return !isNaN(group.productId) && group.variantIds.length > 0;
                });
        });
        return map;
    }

    /* The variant chosen on the PDP, per coupled product id (from the selects rendered by
       nwf-free-gift-info.liquid). Absent on cart pages — there the cart itself is the truth. */
    function pdpChoices() {
        var choices = {};
        document.querySelectorAll('[data-nwf-gift-product]').forEach(function (row) {
            var select = row.querySelector('[data-nwf-gift-select]');
            if (!select) {
                return;
            }
            var value = select.tagName === 'SELECT' ? select.value : select.dataset.value;
            var variantId = parseInt(value, 10);
            if (!isNaN(variantId)) {
                choices[parseInt(row.dataset.nwfGiftProduct.replace(/\D/g, ''), 10)] = variantId;
            }
        });
        return choices;
    }

    function collectSections() {
        var sections = [];
        document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } }),
        );
        return sections;
    }

    function isGift(item) {
        return !!(item.properties && item.properties._free_gift);
    }

    /* The line key identifies a main line: it stays the same across quantity changes and
       differs per variant, selling plan and properties (Meterware `_cut`). If it ever changes,
       the old gift is an orphan and the sync re-adds one for the new key. */
    function lineRef(item) {
        return item.key;
    }

    function themeReady() {
        return !!(window.FoxTheme && FoxTheme.routes && FoxTheme.utils && FoxTheme.pubsub);
    }

    /**
     * Re-renders both carts after our own mutations.
     *
     * `cart:refresh` is NOT enough: only the cart drawer listens for it (cart.js), so on the
     * cart page the removed row stayed until a reload. Publishing `cartUpdate` alone does
     * nothing either — the renderer bails out unless `cart.sections` is filled. So the
     * section HTML is fetched via the Section Rendering API for every section that
     * registered itself, from the CURRENT path (main-cart only renders on /cart).
     */
    function renderCart() {
        var ids = collectSections();
        if (!ids.length) {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            return Promise.resolve();
        }
        return Promise.all([
            fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(','))).then(
                function (response) {
                    return response.json();
                },
            ),
            fetch('/cart.js').then(function (response) {
                return response.json();
            }),
        ])
            .then(function (results) {
                var cart = results[1];
                cart.sections = results[0];
                FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
            })
            .catch(function (error) {
                console.error('[nwf-free-gift] re-render failed, falling back to cart:refresh', error);
                document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            });
    }

    /* ------------------------------------------------------------------ busy states */

    /* Labels come from the locale seeds via a data attribute (nwf-free-gift-cart.liquid) —
       the JS stays free of hardcoded copy. */
    function pendingLabel() {
        var holder = document.querySelector('[data-nwf-free-gift-labels]');
        return (holder && holder.dataset.pending) || '';
    }

    /* Marks a cart line as busy and, for a pending gift, appends a spinner with a label so
       the customer sees that something is still happening. */
    function markBusy(item, kind) {
        if (!item) {
            return;
        }
        item.classList.add('nwf-free-gift-busy');
        if (kind !== 'pending' || item.querySelector('[data-nwf-free-gift-spinner]')) {
            return;
        }
        var hint = document.createElement('span');
        hint.className = 'nwf-free-gift-pending';
        hint.setAttribute('data-nwf-free-gift-spinner', '');
        hint.setAttribute('role', 'status');
        hint.innerHTML = '<span class="nwf-free-gift-spinner" aria-hidden="true"></span>';
        hint.appendChild(document.createTextNode(pendingLabel()));
        var anchor = item.querySelector('.cart-item__details, .cart-item__product--info') || item;
        anchor.appendChild(hint);
    }

    /* Shows the busy state for a planned set of mutations, before they run. */
    function markPlanBusy(mutations) {
        mutations.forEach(function (mutation) {
            if (mutation.kind === 'add') {
                /* No gift row yet — the spinner belongs on the main product line whose
                   gift is on its way. */
                document.querySelectorAll('[data-nwf-free-gift-source]').forEach(function (source) {
                    if (source.dataset.nwfRef === mutation.ref) {
                        markBusy(source.closest('.cart-item'), 'pending');
                    }
                });
                return;
            }
            document.querySelectorAll('[data-nwf-free-gift]').forEach(function (marker) {
                markBusy(marker.closest('.cart-item'), mutation.kind);
            });
        });
    }

    /* ------------------------------------------------------------------ cart mutations */

    function assertOk(response) {
        if (!response.ok) {
            var error = new Error('cart request failed with status ' + response.status);
            error.status = response.status;
            throw error;
        }
        return response;
    }

    function changeLine(key, quantity) {
        var body = JSON.stringify({ id: key, quantity: quantity, sections: collectSections() });
        return fetch(
            FoxTheme.routes.cart_change_url,
            Object.assign({}, FoxTheme.utils.fetchConfig(), { body: body }),
        ).then(assertOk);
    }

    /* Sets several lines at once (key → quantity). One request means the cart never passes
       through a state where a gift is orphaned, which would otherwise surface a validation
       error and flicker the gift line before the sync catches up. */
    function updateLines(updates) {
        var body = JSON.stringify({ updates: updates, sections: collectSections() });
        return fetch(
            FoxTheme.routes.cart_update_url,
            Object.assign({}, FoxTheme.utils.fetchConfig(), { body: body }),
        ).then(assertOk);
    }

    function addGift(variantId, sourceHandle, quantity, sourceRef) {
        var properties = { _free_gift: sourceHandle };
        if (sourceRef) {
            properties._free_gift_for = sourceRef;
        }
        var body = JSON.stringify({
            items: [{ id: variantId, quantity: quantity || 1, properties: properties }],
            sections: collectSections(),
        });
        return fetch(FoxTheme.routes.cart_add_url, Object.assign({}, FoxTheme.utils.fetchConfig(), { body: body }))
            .then(assertOk)
            .catch(function (error) {
                /* Silent degrade (A6): an unaddable gift (sold out, draft, not published
                   to the channel) must never break the cart or loop the sync. */
                blockVariant(variantId, 'add failed: ' + error.message);
                throw error;
            });
    }

    /* ------------------------------------------------------------------ sync */

    /**
     * Builds the list of cart mutations needed to reach the target state.
     *
     * One gift line per main line (NWF-214), so each gift sits under its own colour/cut and
     * leaves with it. Its quantity follows that line (2× machine → 2× brochure). The Cart
     * Transform pools the entitlement per promotion, so the split across lines is free to
     * choose; a manually raised gift quantity stays partly billable rather than free.
     */
    function planMutations(cart, map) {
        var flags = blockedFlags();
        var choices = pdpChoices();
        var mutations = [];

        /* Gift variant already chosen per main line and coupled product (PDP select or a swap
           in the cart). `anyOf` keeps a new line consistent with an earlier choice. */
        var ownGift = {};
        var anyOf = {};
        cart.items.forEach(function (item) {
            if (!isGift(item)) {
                return;
            }
            anyOf[item.product_id] = item.variant_id;
            if (item.properties._free_gift_for) {
                ownGift[item.properties._free_gift_for + '|' + item.product_id] = item.variant_id;
            }
        });

        var wanted = {}; // "ref|variantId" -> { ref, source, variantId, quantity }
        var singleTaken = {};
        cart.items.forEach(function (item) {
            if (isGift(item)) {
                return; // gifts never entitle further gifts
            }
            var ref = lineRef(item);
            (map[item.handle] || []).forEach(function (group) {
                var allowed = group.variantIds.filter(function (id) {
                    return !flags[id];
                });
                if (!allowed.length) {
                    return;
                }
                // Single unit gifts (videos) exist once per cart, on the first entitled line.
                if (group.singleUnit) {
                    if (singleTaken[group.productId]) {
                        return;
                    }
                    singleTaken[group.productId] = true;
                }
                var candidates = [
                    ownGift[ref + '|' + group.productId],
                    choices[group.productId],
                    anyOf[group.productId],
                ];
                var target = allowed[0]; // default: first variant the merchant listed
                for (var i = 0; i < candidates.length; i += 1) {
                    if (allowed.indexOf(candidates[i]) !== -1) {
                        target = candidates[i];
                        break;
                    }
                }
                var key = ref + '|' + target;
                if (!wanted[key]) {
                    wanted[key] = { ref: ref, source: item.handle, variantId: target, quantity: 0 };
                }
                wanted[key].quantity = group.singleUnit ? 1 : wanted[key].quantity + item.quantity;
            });
        });

        var present = {};
        var notFree = [];
        cart.items.forEach(function (item) {
            if (!isGift(item)) {
                return;
            }
            /* Lines without `_free_gift_for` predate NWF-214 and never match: they are removed
               and re-added per main line. */
            var key = (item.properties._free_gift_for || '') + '|' + item.variant_id;
            var target = wanted[key];
            if (!target || present[key]) {
                mutations.push({ kind: 'remove', key: item.key });
                return;
            }
            present[key] = true;
            if (item.quantity !== target.quantity) {
                mutations.push({ kind: 'requantify', key: item.key, quantity: target.quantity });
                return;
            }
            if (item.final_price > 0) {
                notFree.push(item);
            }
        });

        Object.keys(wanted).forEach(function (key) {
            if (!present[key]) {
                mutations.push({
                    kind: 'add',
                    variantId: Number(wanted[key].variantId),
                    source: wanted[key].source,
                    ref: wanted[key].ref,
                    quantity: wanted[key].quantity,
                });
            }
        });

        /* Price is only judged on a settled cart: while other gift lines still carry surplus
           units, the pooled entitlement may be spent on them and a correct line looks billable.
           Never charge for a gift: remove and block the variant (A6). */
        if (!mutations.length) {
            notFree.forEach(function (item) {
                blockVariant(item.variant_id, 'not free, final_price ' + item.final_price);
                mutations.push({ kind: 'remove', key: item.key });
            });
        }

        return mutations;
    }

    /* Runs all mutations sequentially; failures are logged and skipped (the failing add
       flags its variant). Resolves with the number of SUCCESSFUL mutations — only those
       justify a re-render/re-sync round. */
    function runMutations(mutations) {
        return mutations.reduce(function (chain, mutation) {
            return chain.then(function (succeeded) {
                var step;
                if (mutation.kind === 'remove') {
                    step = changeLine(mutation.key, 0);
                } else if (mutation.kind === 'requantify') {
                    step = changeLine(mutation.key, mutation.quantity);
                } else {
                    step = addGift(mutation.variantId, mutation.source, mutation.quantity, mutation.ref);
                }
                return step
                    .then(function () {
                        return succeeded + 1;
                    })
                    .catch(function (error) {
                        console.error('[nwf-free-gift] mutation failed', mutation, error);
                        return succeeded;
                    });
            });
        }, Promise.resolve(0));
    }

    var syncing = false;
    var resyncQueued = false;

    function sync() {
        if (!themeReady()) {
            return;
        }
        var map = readCouplingMap();
        if (syncing) {
            resyncQueued = true;
            return;
        }
        syncing = true;

        fetch('/cart.js')
            .then(function (response) {
                return response.json();
            })
            .then(function (cart) {
                var mutations = planMutations(cart, map);
                if (!mutations.length) {
                    return false;
                }
                markPlanBusy(mutations);
                return runMutations(mutations).then(function (succeeded) {
                    return succeeded > 0;
                });
            })
            .then(function (mutated) {
                if (!mutated) {
                    return null;
                }
                return renderCart();
            })
            .catch(function (error) {
                console.error('[nwf-free-gift] cart sync failed', error);
            })
            .then(function () {
                syncing = false;
                if (resyncQueued) {
                    resyncQueued = false;
                    sync();
                }
            });
    }

    /* ------------------------------------------------------------------ line styling */

    /* Marks gift cart items (class = :has() fallback for the hidden quantity stepper and
       remove control, see nwf-free-gift.css) and moves each one directly below its main
       product line, indented. Shopify orders cart lines by when they were added, which
       puts a gift nowhere near its main product once the cart holds several products —
       so the visual grouping is done here, in both the drawer and the cart page. */
    function markGiftLines() {
        document.querySelectorAll('[data-nwf-free-gift]').forEach(function (marker) {
            var item = marker.closest('.cart-item');
            if (!item || item.dataset.nwfGiftProcessed === '1') {
                /* Once per rendered line. Without this flag every unrelated DOM mutation
                   (the delivery countdown ticks every second) would re-run the moving and
                   element swapping below, whose own mutations feed the observer again —
                   a permanent DOM churn that shows up in the inspector. A cart re-render
                   creates fresh elements without the flag, so state stays correct. */
                return;
            }
            item.dataset.nwfGiftProcessed = '1';
            item.classList.add('nwf-free-gift-line');

            /* Fallback for browsers without CSS :has() — the grey variant name is hidden
               when a swap select shows the same information (see nwf-free-gift.css). */
            if (item.querySelector('.nwf-free-gift-swap')) {
                item.classList.add('nwf-free-gift-has-swap');
            }

            /* The quantity stays visible (how many gifts are included) but must not be
               editable: it mirrors the main product's quantity. CSS alone would not stop
               keyboard input, so the controls are really disabled. The variant select is
               deliberately NOT disabled — swapping the colour is allowed. */
            item.querySelectorAll('quantity-input input, quantity-input button').forEach(function (control) {
                control.disabled = true;
                control.setAttribute('aria-disabled', 'true');
            });

            var sourceHandle = marker.dataset.nwfFreeGift;
            if (!sourceHandle) {
                return;
            }
            /* The main product line of this gift, found via the source marker rather than
               a data attribute on the line: the drawer's <li> carries data-handle, the
               cart page's <tr> does not — the marker is rendered in both. */
            var sourceRef = marker.dataset.nwfFreeGiftFor;
            var main = null;
            document.querySelectorAll('[data-nwf-free-gift-source]').forEach(function (source) {
                var matches = sourceRef
                    ? source.dataset.nwfRef === sourceRef
                    : source.dataset.nwfHandle === sourceHandle;
                if (main || !matches) {
                    return;
                }
                var candidate = source.closest('.cart-item');
                if (candidate && candidate !== item && candidate.parentElement === item.parentElement) {
                    main = candidate;
                }
            });
            /* Insert after the main line and after any gift lines already moved there,
               so several gifts of one product keep a stable order. */
            if (main) {
                var anchor = main;
                while (
                    anchor.nextElementSibling &&
                    anchor.nextElementSibling.classList.contains('nwf-free-gift-line')
                ) {
                    anchor = anchor.nextElementSibling;
                }
                if (anchor !== item && anchor.nextElementSibling !== item) {
                    anchor.insertAdjacentElement('afterend', item);
                }
            }
        });

        /**
         * Lines whose product is not purchasable on its own: no PDP link.
         *
         * The anchor is REPLACED by a span rather than stripped of its href. A hrefless
         * <a> still looks and reads like a link — link colour, the theme's underline
         * animation, "link" to a screen reader — but does nothing on hover or click, which
         * reads as broken. A span with the same classes minus `reversed-link` keeps the
         * layout and typography and is honestly not interactive.
         */
        document.querySelectorAll('[data-nwf-not-purchasable-line]').forEach(function (marker) {
            var item = marker.closest('.cart-item');
            if (!item || item.dataset.nwfBlockProcessed === '1') {
                return; // once per rendered line, see the gift branch above
            }
            item.dataset.nwfBlockProcessed = '1';
            item.classList.add('nwf-not-purchasable-line');
            item.querySelectorAll('a.cart-item__title, a.cart-item__media').forEach(function (link) {
                var plain = document.createElement('span');
                plain.className = link.className.replace(/\breversed-link\b/, '').trim();
                plain.innerHTML = link.innerHTML;
                link.replaceWith(plain);
            });
        });
    }

    /* ---------------------------------------------------- atomic removal of a main line */

    /**
     * Removing a main product must take its gifts with it in the SAME request.
     *
     * Letting the theme remove only the main line leaves the gift orphaned until the sync
     * runs: the customer sees the gift line flash and — before the validation was limited
     * to the checkout steps — got a cart error for it. Both disappear when the removal is
     * atomic.
     *
     * Capture phase, because the theme's own click handler on the remove control would
     * otherwise fire first.
     */
    function onRemoveClick(event) {
        if (!themeReady()) {
            return;
        }
        var control = event.target && event.target.closest ? event.target.closest('.btn-remove') : null;
        if (!control) {
            return;
        }
        var item = control.closest('.cart-item');
        if (!item || item.classList.contains('nwf-free-gift-line')) {
            return; // gift lines have no remove control anyway
        }
        var source = item.querySelector('[data-nwf-free-gift-source]');
        if (!source) {
            return; // no coupling on this product → let the theme handle it
        }
        var handle = source.dataset.nwfHandle;
        var ref = source.dataset.nwfRef;

        event.preventDefault();
        event.stopPropagation();

        /* Immediate feedback: the row and its gift rows fade and stop reacting while the
           request runs, so the click is visibly acknowledged. */
        markBusy(item, 'remove');
        document.querySelectorAll('[data-nwf-free-gift]').forEach(function (marker) {
            if (marker.dataset.nwfFreeGiftFor === ref) {
                markBusy(marker.closest('.cart-item'), 'remove');
            }
        });

        /* Only the clicked line and its own gifts: other colours of the same product share the
           handle and must stay (NWF-214). */
        fetch('/cart.js')
            .then(function (response) {
                return response.json();
            })
            .then(function (cart) {
                var updates = {};
                var handleRemains = false;
                cart.items.forEach(function (line) {
                    if (!isGift(line)) {
                        if (lineRef(line) === ref) {
                            updates[line.key] = 0;
                        } else if (line.handle === handle) {
                            handleRemains = true;
                        }
                        return;
                    }
                    if (line.properties._free_gift_for === ref) {
                        updates[line.key] = 0;
                    }
                });
                // Pre NWF-214 gift lines carry no line ref: they go with the last line of their product.
                cart.items.forEach(function (line) {
                    if (isGift(line) && !line.properties._free_gift_for) {
                        if (line.properties._free_gift === handle && !handleRemains) {
                            updates[line.key] = 0;
                        }
                    }
                });
                if (!Object.keys(updates).length) {
                    return null;
                }
                return updateLines(updates).then(renderCart);
            })
            .catch(function (error) {
                console.error('[nwf-free-gift] atomic removal failed', error);
                /* Never leave the cart looking frozen on error. */
                document.querySelectorAll('.nwf-free-gift-busy').forEach(function (busy) {
                    busy.classList.remove('nwf-free-gift-busy');
                });
            });
    }

    /* ------------------------------------------------- variant choice interactions */

    /**
     * PDP: the customer picked another variant before adding to cart.
     *
     * Nothing to do beyond re-running the sync — `pdpChoices()` reads the select on the
     * next run. Relevant when the gift is already in the cart: the sync then removes the
     * old variant line and adds the chosen one.
     */
    function onPdpChoice(event) {
        var select = event.target;
        if (!select || !select.matches || !select.matches('[data-nwf-gift-select]')) {
            return;
        }
        sync();
    }

    /**
     * Cart: the customer swapped the colour of a gift line.
     *
     * A line item's variant cannot be changed in place, so the line is replaced: remove the
     * old one and add the chosen variant with the same `_free_gift` property, in that order,
     * then re-render. The quantity is carried over so a swap does not reset it.
     */
    function onCartSwap(event) {
        var select = event.target;
        if (!select || !select.matches || !select.matches('[data-nwf-gift-swap]')) {
            return;
        }
        if (!themeReady()) {
            return;
        }
        var variantId = parseInt(select.value, 10);
        var lineKey = select.dataset.nwfLineKey;
        var source = select.dataset.nwfSource;
        if (isNaN(variantId) || !lineKey || !source) {
            return;
        }

        var item = select.closest('.cart-item');
        markBusy(item, 'swap');

        fetch('/cart.js')
            .then(function (response) {
                return response.json();
            })
            .then(function (cart) {
                var line = null;
                cart.items.forEach(function (candidate) {
                    if (candidate.key === lineKey) {
                        line = candidate;
                    }
                });
                var quantity = line ? line.quantity : 1;
                var sourceRef = line && line.properties ? line.properties._free_gift_for : '';
                return changeLine(lineKey, 0)
                    .then(function () {
                        return addGift(variantId, source, quantity, sourceRef);
                    })
                    .then(renderCart);
            })
            .catch(function (error) {
                console.error('[nwf-free-gift] variant swap failed', error);
                if (item) {
                    item.classList.remove('nwf-free-gift-busy');
                }
            });
    }

    /* ------------------------------------------------------------------ init */

    var scheduled = false;

    /* Cheap, network-free: re-mark gift lines after theme re-renders. */
    function scheduleMarking() {
        if (scheduled) {
            return;
        }
        scheduled = true;
        window.requestAnimationFrame(function () {
            scheduled = false;
            markGiftLines();
        });
    }

    function init() {
        if (window.__nwfFreeGiftInit) {
            return;
        }
        window.__nwfFreeGiftInit = true;

        markGiftLines();
        sync();

        /* The theme publishes cartUpdate after every cart mutation (add, change,
           drawer re-render) — including our own, which makes the sync converge:
           the follow-up run finds no mutations and stays silent. */
        if (themeReady()) {
            FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function () {
                /* Some publishers fire before the section HTML is swapped in; run once
                   right after the current frame and once more shortly after, so the
                   coupling markers in the DOM are guaranteed to be current. */
                window.requestAnimationFrame(sync);
                window.setTimeout(sync, 600);
                /* Marking is driven by the same event, not only by the observer below: a
                   cart re-render can replace the observed <cart-drawer>/<cart-items>
                   element itself, and an observer on a detached node reports nothing —
                   that is why gift lines in the drawer stayed unmarked and ungrouped. */
                scheduleMarking();
                window.setTimeout(markGiftLines, 600);
            });
        }

        document.addEventListener('click', onRemoveClick, true);
        document.addEventListener('change', onPdpChoice);
        document.addEventListener('change', onCartSwap);

        /* Scoped to the cart containers instead of <body>: the theme swaps their innerHTML
           on cart updates, which is the only reason this observer exists. Watching the
           whole document would react to every unrelated widget (countdowns, sliders). */
        var observer = new MutationObserver(scheduleMarking);
        var roots = document.querySelectorAll('cart-items, cart-drawer, main-cart');
        if (roots.length) {
            roots.forEach(function (root) {
                observer.observe(root, { childList: true, subtree: true });
            });
        } else {
            observer.observe(document.body, { childList: true, subtree: true });
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
